import React, { useEffect, useState } from "react";
import { useHttpClient } from "../../../../../shared/hooks/http-hook";
import { useLoading } from "../../../../../shared/context/LoadingContext";
import moment from "moment";
import "moment/locale/pt-br";
import toast from "react-hot-toast";
import XLSX from "xlsx-js-style";

// Material UI imports
import {
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import WarningIcon from "@mui/icons-material/Warning";
import PeopleIcon from "@mui/icons-material/People";
import MoneyOffIcon from "@mui/icons-material/MoneyOff";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Link } from "react-router-dom";

// Para gráficos
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";

// Configurar locale do moment para português
moment.locale("pt-br");

// Cores
const COLORS = ["#d32f2f", "#f44336", "#e57373", "#ef5350"];

// Componentes estilizados
const SummaryCard = styled(Card)(({ theme, cardtype }) => ({
  height: "100%",
  backgroundColor:
    cardtype === "total"
      ? "rgba(211, 47, 47, 0.08)"
      : cardtype === "clientes"
      ? "rgba(244, 67, 54, 0.08)"
      : cardtype === "maior"
      ? "rgba(229, 115, 115, 0.08)"
      : "rgba(239, 83, 80, 0.08)",
  borderLeft: `4px solid ${
    cardtype === "total"
      ? theme.palette.error.dark
      : cardtype === "clientes"
      ? theme.palette.error.main
      : cardtype === "maior"
      ? theme.palette.error.light
      : "#ef5350"
  }`,
}));

const RiskBadge = styled(Chip)(({ theme, risklevel }) => ({
  backgroundColor:
    risklevel >= 100
      ? "#d32f2f"
      : risklevel >= 75
      ? "#f44336"
      : risklevel >= 50
      ? "#ff5722"
      : "#ff9800",
  color: "#fff",
  fontWeight: "bold",
}));

function RelatorioOrcamentosAltoRisco({
  dataFiltro,
  dadosClinica,
  auth,
  acao,
  atualizacoes,
}) {
  const { startLoading, stopLoading } = useLoading();
  const { sendRequest } = useHttpClient();
  const [dados, setDados] = useState(null);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    if (acao) {
      if (acao === "gerar") {
        gerarRelatorio();
      } else if (acao === "exportar") {
        exportarFicheiro();
      }
    }
  }, [acao, atualizacoes]);

  const gerarRelatorio = async () => {
    if (!dadosClinica?._id) return;

    setError(null);
    startLoading();
    try {
      const responseData = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/contascorrentes/getRelatorioOrcamentosAltoRisco/${dadosClinica._id}`,
        "GET",
        null,
        {
          Authorization: "Bearer " + auth.token,
        }
      );

      setDados(responseData.data);
    } catch (err) {
      console.error("Erro ao gerar relatório de alto risco:", err);
      setError(
        err.message || "Ocorreu um erro ao gerar o relatório de alto risco."
      );
    } finally {
      stopLoading();
    }
  };

  const exportarFicheiro = () => {
    if (!dados) {
      toast.error("É necessário gerar o relatório primeiro!");
      return;
    }

    try {
      const wb = XLSX.utils.book_new();

      // Planilha de resumo
      const resumoData = [
        ["RELATÓRIO DE ORÇAMENTOS DE ALTO RISCO"],
        [`Data de Geração: ${moment().format("DD/MM/YYYY HH:mm")}`],
        [`Clínica: ${dadosClinica?.nome || ""}`],
        [],
        ["ESTATÍSTICAS GERAIS"],
        ["Total de Clientes em Alto Risco", dados.estatisticas.totalClientes],
        [
          "Valor Total em Risco",
          `${dados.estatisticas.valorTotalRisco.toLocaleString("pt-BR")} CVE`,
        ],
        [
          "Valor Total Pago",
          `${dados.estatisticas.valorTotalPago.toLocaleString("pt-BR")} CVE`,
        ],
        [
          "Valor Total Executado",
          `${dados.estatisticas.valorTotalExecutado.toLocaleString(
            "pt-BR"
          )} CVE`,
        ],
        [
          "Maior Risco Individual",
          `${dados.estatisticas.maiorRisco.toLocaleString("pt-BR")} CVE`,
        ],
        [
          "Menor Risco Individual",
          `${dados.estatisticas.menorRisco.toLocaleString("pt-BR")} CVE`,
        ],
        [
          "Percentual Médio de Risco",
          `${dados.estatisticas.percentualMedioRisco.toFixed(2)}%`,
        ],
        [],
        ["DEFINIÇÃO DE ALTO RISCO"],
        [
          "Clientes que receberam mais de 50% a mais de tratamento do que pagaram",
        ],
      ];

      const wsResumo = XLSX.utils.aoa_to_sheet(resumoData);

      // Planilha de clientes em risco
      const clientesHeader = [
        [
          "#",
          "Cliente",
          "Contacto",
          "Email",
          "Valor Pago",
          "Valor Executado",
          "Diferença (Risco)",
          "% Risco",
          "Nº Orçamentos",
        ],
      ];

      const clientesData = dados.clientes.map((item, index) => [
        index + 1,
        item.cliente.nome,
        item.cliente.contacto || "-",
        item.cliente.email || "-",
        item.valorPago,
        item.valorExecutado,
        item.diferenca,
        `${item.percentualRisco}%`,
        item.orcamentos.length,
      ]);

      const wsClientes = XLSX.utils.aoa_to_sheet([
        ...clientesHeader,
        ...clientesData,
      ]);

      // Planilha de detalhes por orçamento
      const orcamentosHeader = [
        [
          "#",
          "Cliente",
          "Orçamento",
          "Código",
          "Valor Total Orçamento",
          "Valor Pago",
          "Valor Executado",
          "Diferença",
        ],
      ];

      let orcamentosData = [];
      dados.clientes.forEach((cliente, clienteIndex) => {
        cliente.orcamentos.forEach((orc) => {
          orcamentosData.push([
            clienteIndex + 1,
            cliente.cliente.nome,
            orc.numero || "-",
            orc.codigo || "-",
            orc.valorTotal,
            orc.valorPago,
            orc.valorExecutado,
            orc.diferenca,
          ]);
        });
      });

      const wsOrcamentos = XLSX.utils.aoa_to_sheet([
        ...orcamentosHeader,
        ...orcamentosData,
      ]);

      // Aplicar estilos
      wsResumo["A1"].s = {
        font: { bold: true, sz: 16 },
        alignment: { horizontal: "center" },
      };

      wsResumo["A5"].s = {
        font: { bold: true, sz: 14 },
        fill: { fgColor: { rgb: "E6E6E6" } },
      };

      wsResumo["A13"].s = {
        font: { bold: true, sz: 14 },
        fill: { fgColor: { rgb: "E6E6E6" } },
      };

      // Cabeçalhos
      for (let i = 0; i < clientesHeader[0].length; i++) {
        const cell = XLSX.utils.encode_cell({ r: 0, c: i });
        wsClientes[cell].s = {
          font: { bold: true, color: { rgb: "FFFFFF" } },
          fill: { fgColor: { rgb: "D32F2F" } },
          alignment: { horizontal: "center" },
        };
      }

      for (let i = 0; i < orcamentosHeader[0].length; i++) {
        const cell = XLSX.utils.encode_cell({ r: 0, c: i });
        wsOrcamentos[cell].s = {
          font: { bold: true, color: { rgb: "FFFFFF" } },
          fill: { fgColor: { rgb: "D32F2F" } },
          alignment: { horizontal: "center" },
        };
      }

      // Adicionar planilhas
      XLSX.utils.book_append_sheet(wb, wsResumo, "Resumo");
      XLSX.utils.book_append_sheet(wb, wsClientes, "Clientes em Alto Risco");
      XLSX.utils.book_append_sheet(wb, wsOrcamentos, "Detalhes por Orçamento");

      // Configurar colunas
      wsResumo["!cols"] = [{ wch: 40 }, { wch: 20 }];
      wsClientes["!cols"] = [
        { wch: 5 },
        { wch: 30 },
        { wch: 15 },
        { wch: 25 },
        { wch: 15 },
        { wch: 15 },
        { wch: 18 },
        { wch: 12 },
        { wch: 15 },
      ];
      wsOrcamentos["!cols"] = [
        { wch: 5 },
        { wch: 30 },
        { wch: 15 },
        { wch: 15 },
        { wch: 18 },
        { wch: 15 },
        { wch: 15 },
        { wch: 15 },
      ];

      wsResumo["!merges"] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: 1 } },
        { s: { r: 1, c: 0 }, e: { r: 1, c: 1 } },
        { s: { r: 2, c: 0 }, e: { r: 2, c: 1 } },
      ];

      XLSX.writeFile(
        wb,
        `orcamentos_alto_risco_${moment().format("YYYY-MM-DD")}.xlsx`
      );

      toast.success("Arquivo exportado com sucesso!");
    } catch (err) {
      console.error("Erro ao exportar arquivo:", err);
      toast.error("Erro ao exportar arquivo");
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Prepara dados para gráfico de distribuição de risco
  const prepareRiscoDistribuicaoData = () => {
    if (!dados?.clientes) return [];

    const faixas = [
      { label: "50-75%", min: 50, max: 75, count: 0 },
      { label: "75-100%", min: 75, max: 100, count: 0 },
      { label: "100-150%", min: 100, max: 150, count: 0 },
      { label: "Acima de 150%", min: 150, max: Infinity, count: 0 },
    ];

    dados.clientes.forEach((cliente) => {
      const risco = cliente.percentualRisco;
      const faixa = faixas.find((f) => risco >= f.min && risco < f.max);
      if (faixa) faixa.count++;
    });

    return faixas.map((f, index) => ({
      name: f.label,
      value: f.count,
      color: COLORS[index],
    }));
  };

  // Top 5 maiores riscos
  const prepareTop5RiscosData = () => {
    if (!dados?.clientes) return [];

    return dados.clientes.slice(0, 5).map((item) => ({
      nome: item.cliente.nome.split(" ")[0],
      risco: Math.abs(item.diferenca),
    }));
  };

  const formatCurrency = (value) => {
    return (
      value?.toLocaleString("pt-BR", {
        style: "currency",
        currency: "CVE",
      }) || "0 CVE"
    );
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography
          variant="h5"
          gutterBottom
          component="div"
          sx={{ fontWeight: "bold" }}
        >
          Relatório de Orçamentos de Alto Risco
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1">
            Clínica: {dadosClinica?.nome || ""}
          </Typography>
          <Typography variant="subtitle2" color="text.secondary">
            Clientes que receberam mais de 50% a mais de tratamento do que
            pagaram
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {!dados && !error && (
          <Box sx={{ display: "flex", justifyContent: "center", my: 5 }}>
            <CircularProgress />
          </Box>
        )}

        {dados && (
          <>
            {dados.clientes.length === 0 ? (
              <Alert severity="success" sx={{ my: 3 }}>
                Excelente! Não há clientes em situação de alto risco financeiro.
              </Alert>
            ) : (
              <>
                {/* Cards de resumo */}
                <Grid container spacing={3} sx={{ mb: 4 }}>
                  <Grid item xs={12} sm={6} md={3}>
                    <SummaryCard cardtype="clientes">
                      <CardContent>
                        <Box
                          sx={{ display: "flex", alignItems: "center", mb: 1 }}
                        >
                          <WarningIcon color="error" sx={{ mr: 1 }} />
                          <Typography variant="h6">
                            Clientes em Risco
                          </Typography>
                        </Box>
                        <Typography
                          variant="h4"
                          component="div"
                          color="error.main"
                          sx={{ fontWeight: "bold", mb: 1 }}
                        >
                          {dados.estatisticas.totalClientes}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Alto risco financeiro
                        </Typography>
                      </CardContent>
                    </SummaryCard>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <SummaryCard cardtype="total">
                      <CardContent>
                        <Box
                          sx={{ display: "flex", alignItems: "center", mb: 1 }}
                        >
                          <MoneyOffIcon color="error" sx={{ mr: 1 }} />
                          <Typography variant="h6">Valor em Risco</Typography>
                        </Box>
                        <Typography
                          variant="h4"
                          component="div"
                          color="error.dark"
                          sx={{ fontWeight: "bold", mb: 1 }}
                        >
                          {formatCurrency(dados.estatisticas.valorTotalRisco)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total a recuperar
                        </Typography>
                      </CardContent>
                    </SummaryCard>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <SummaryCard cardtype="maior">
                      <CardContent>
                        <Box
                          sx={{ display: "flex", alignItems: "center", mb: 1 }}
                        >
                          <TrendingDownIcon color="error" sx={{ mr: 1 }} />
                          <Typography variant="h6">Maior Risco</Typography>
                        </Box>
                        <Typography
                          variant="h4"
                          component="div"
                          color="error.light"
                          sx={{ fontWeight: "bold", mb: 1 }}
                        >
                          {formatCurrency(dados.estatisticas.maiorRisco)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Individual
                        </Typography>
                      </CardContent>
                    </SummaryCard>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <SummaryCard cardtype="media">
                      <CardContent>
                        <Box
                          sx={{ display: "flex", alignItems: "center", mb: 1 }}
                        >
                          <PeopleIcon color="error" sx={{ mr: 1 }} />
                          <Typography variant="h6">% Médio de Risco</Typography>
                        </Box>
                        <Typography
                          variant="h4"
                          component="div"
                          sx={{ fontWeight: "bold", mb: 1, color: "#ef5350" }}
                        >
                          {dados.estatisticas.percentualMedioRisco.toFixed(0)}%
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Por cliente
                        </Typography>
                      </CardContent>
                    </SummaryCard>
                  </Grid>
                </Grid>

                {/* Gráficos */}
                <Grid container spacing={3} sx={{ mb: 4 }}>
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2, height: "100%" }}>
                      <Typography variant="h6" gutterBottom>
                        Distribuição por Nível de Risco
                      </Typography>
                      <Box sx={{ height: 300 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={prepareRiscoDistribuicaoData()}
                              cx="50%"
                              cy="50%"
                              labelLine={false}
                              outerRadius={80}
                              fill="#8884d8"
                              dataKey="value"
                              label={({
                                cx,
                                cy,
                                midAngle,
                                innerRadius,
                                outerRadius,
                                percent,
                              }) => {
                                const radius =
                                  innerRadius +
                                  (outerRadius - innerRadius) * 0.5;
                                const x =
                                  cx +
                                  radius *
                                    Math.cos(-midAngle * (Math.PI / 180));
                                const y =
                                  cy +
                                  radius *
                                    Math.sin(-midAngle * (Math.PI / 180));
                                return (
                                  <text
                                    x={x}
                                    y={y}
                                    fill="#fff"
                                    textAnchor={x > cx ? "start" : "end"}
                                    dominantBaseline="central"
                                  >
                                    {`${(percent * 100).toFixed(0)}%`}
                                  </text>
                                );
                              }}
                            >
                              {prepareRiscoDistribuicaoData().map(
                                (entry, index) => (
                                  <Cell
                                    key={`cell-${index}`}
                                    fill={entry.color}
                                  />
                                )
                              )}
                            </Pie>
                            <Legend
                              layout="vertical"
                              verticalAlign="middle"
                              align="right"
                            />
                            <RechartsTooltip />
                          </PieChart>
                        </ResponsiveContainer>
                      </Box>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2, height: "100%" }}>
                      <Typography variant="h6" gutterBottom>
                        Top 5 Maiores Riscos
                      </Typography>
                      <Box sx={{ height: 300 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            data={prepareTop5RiscosData()}
                            margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="nome" />
                            <YAxis />
                            <RechartsTooltip />
                            <Bar
                              dataKey="risco"
                              name="Valor em Risco (CVE)"
                              fill="#d32f2f"
                            />
                          </BarChart>
                        </ResponsiveContainer>
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>

                {/* Tabela de clientes */}
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Detalhes dos Clientes em Alto Risco
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Cliente</TableCell>
                          <TableCell align="right">Valor Pago</TableCell>
                          <TableCell align="right">Valor Executado</TableCell>
                          <TableCell align="right">Risco</TableCell>
                          <TableCell align="center">% Risco</TableCell>
                          <TableCell align="center">Orçamentos</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {dados.clientes
                          .slice(
                            page * rowsPerPage,
                            page * rowsPerPage + rowsPerPage
                          )
                          .map((item) => (
                            <TableRow key={item.cliente.id} hover>
                              <TableCell>
                                <Link
                                  to={`/clientes/${item.cliente.id}-debitos`}
                                  style={{ textDecoration: "none" }}
                                >
                                  <Typography
                                    variant="body2"
                                    fontWeight="bold"
                                    sx={{
                                      color: "primary.main",
                                      "&:hover": {
                                        textDecoration: "underline",
                                      },
                                    }}
                                  >
                                    {item.cliente.nome}
                                  </Typography>
                                </Link>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {item.cliente.contacto || "Sem contacto"}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                {formatCurrency(item.valorPago)}
                              </TableCell>
                              <TableCell align="right">
                                {formatCurrency(item.valorExecutado)}
                              </TableCell>
                              <TableCell align="right">
                                <Typography
                                  variant="body2"
                                  fontWeight="bold"
                                  color="error.main"
                                >
                                  {formatCurrency(Math.abs(item.diferenca))}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                <RiskBadge
                                  label={`${item.percentualRisco}%`}
                                  size="small"
                                  risklevel={item.percentualRisco}
                                />
                              </TableCell>
                              <TableCell align="center">
                                <Accordion>
                                  <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                  >
                                    <Typography variant="body2">
                                      {item.orcamentos.length} orçamento
                                      {item.orcamentos.length > 1 ? "s" : ""}
                                    </Typography>
                                  </AccordionSummary>
                                  <AccordionDetails>
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow>
                                          <TableCell>Código</TableCell>
                                          <TableCell align="right">
                                            Pago
                                          </TableCell>
                                          <TableCell align="right">
                                            Executado
                                          </TableCell>
                                          <TableCell align="right">
                                            Diferença
                                          </TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {item.orcamentos.map((orc) => (
                                          <TableRow key={orc.id}>
                                            <TableCell>
                                              {orc.codigo || orc.numero}
                                            </TableCell>
                                            <TableCell align="right">
                                              {formatCurrency(orc.valorPago)}
                                            </TableCell>
                                            <TableCell align="right">
                                              {formatCurrency(
                                                orc.valorExecutado
                                              )}
                                            </TableCell>
                                            <TableCell
                                              align="right"
                                              sx={{
                                                color:
                                                  orc.diferenca < 0
                                                    ? "error.main"
                                                    : "success.main",
                                              }}
                                            >
                                              {formatCurrency(orc.diferenca)}
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </AccordionDetails>
                                </Accordion>
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    component="div"
                    count={dados.clientes.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage="Linhas por página:"
                    labelDisplayedRows={({ from, to, count }) =>
                      `${from}-${to} de ${count}`
                    }
                  />
                </Paper>
              </>
            )}
          </>
        )}
      </Paper>
    </Box>
  );
}

export default RelatorioOrcamentosAltoRisco;
